// hooks.ts — window.__game dev/test bridge (DEV-HOOK CONTRACT).
//
// Two consumers must agree on this surface:
//   1) The task's stated contract: { ready, version, state(), input(cmd), startBoard(opts) }.
//   2) The Playwright eyes harness (playwright.capture.config.ts staging notes):
//        waits for `window.__game?.scene !== undefined`, then calls
//        `window.__game.tick(dt, steps)`, `window.__game.cheat(name)`, `window.__game.state()`.
// We implement the UNION so both pass. The bridge is installed once and tracks the live Game scene.
import type Phaser from 'phaser';
import type { Snapshot } from './core';
import type { GameScene } from './render/scenes/GameScene';
import { GAME_VERSION } from './version';

type InputCmd = 'up' | 'down' | 'left' | 'right' | 'munch' | 'scan';

export interface GameHook {
  ready: boolean;
  version: string;
  /** current sim snapshot (null only before the first board is live) */
  state(): Snapshot | null;
  /** enqueue a human (PlayerId 0) command */
  input(cmd: InputCmd): void;
  /** boot/replace the active board */
  startBoard(opts: { topic?: string; seed?: number; ai?: boolean; boardIndex?: number; forceDeception?: boolean }): void;
  // ---- eyes-harness extras (synchronous stepping; rAF is throttled headless) ----
  /** the live Game scene (presence is the harness "ready" signal) */
  scene?: GameScene;
  /** advance the sim deterministically by dt(ms-or-seconds) x steps without waiting on rAF */
  tick(dt?: number, steps?: number): void;
  /** dev cheats — GATED behind ?debug=1 (pre-PvP hardening). Returns true when a cheat was dispatched,
   *  false when the cheat surface is disabled (shipped player build) or no scene is live. The method is
   *  ALWAYS present and never throws, so the eyes/parity harness can call it safely regardless of mode. */
  cheat(name: string): boolean;
  /** show/hide the debug FPS overlay so screenshots aren't polluted (capture.mjs calls this) */
  setDebugVisible(visible: boolean): void;
  seed: number;
}

// NOTE: we deliberately do NOT `declare global { interface Window { __game } }` here. The eyes
// spec (tests/eyes/capture.spec.ts) declares its own narrower `Window.__game` shape, and two
// differing global augmentations would collide. We read/write the property through a local cast
// instead, so our richer hook coexists with the test's declaration at typecheck time.
type GameWindow = { __game?: GameHook };
const win = (): GameWindow => window as unknown as GameWindow;

let game: Phaser.Game | null = null;
let activeScene: GameScene | null = null;

function findGameScene(): GameScene | null {
  if (activeScene && (activeScene.scene.isActive() || activeScene.scene.isPaused())) return activeScene;
  if (!game) return null;
  const s = game.scene.getScene('Game') as GameScene | null;
  return s ?? null;
}

/** Install the bridge once, given the Phaser.Game. Scenes call setActiveGameScene when they boot. */
export function installHooks(g: Phaser.Game): void {
  game = g;
  const params = new URLSearchParams(window.location.search);
  const bootSeed = Number(params.get('seed') ?? '11') || 11;
  // ?debug=1 gates the CHEAT surface only (matches registryKeys.parseBootParams). Everything else on
  // this hook stays ungated so every capture path works with debug=0.
  const debug = params.get('debug') === '1';

  const hook: GameHook = {
    ready: false,
    version: GAME_VERSION,
    seed: bootSeed,
    state(): Snapshot | null {
      const s = findGameScene();
      return s?.sim ? s.sim.snapshot() : null;
    },
    input(cmd: InputCmd): void {
      const s = findGameScene();
      s?.dispatch(cmd);
    },
    startBoard(opts): void {
      if (!game) return;
      // Starting 'Game' from the global manager does NOT stop other scenes: a live Menu would keep
      // running underneath — and its DOM topic <input> floats over the canvas, ghosting captures
      // (found 2026-07-01 in lane evidence). Real gameplay flows go through MenuScene's own
      // scene.start (which shuts Menu down); mirror that here for the dev-hook path.
      for (const key of ['Menu', 'Result', 'Splash'] as const) {
        if (game.scene.isActive(key) || game.scene.isPaused(key)) game.scene.stop(key);
      }
      game.scene.start('Game', {
        topic: opts.topic,
        seed: opts.seed,
        ai: opts.ai,
        boardIndex: opts.boardIndex ?? 0,
        forceDeception: opts.forceDeception,
      });
    },
    tick(dt = 1000 / 60, steps = 1): void {
      const s = findGameScene();
      // The eyes harness passes seconds (e.g. 1/60); the sim wants ms. Normalize: <5 => seconds.
      const dtMs = dt < 5 ? dt * 1000 : dt;
      if (s?.sim) s.sim.tick(dtMs, steps);
    },
    cheat(name: string): boolean {
      // CHEAT-SURFACE SPLIT (pre-PvP hardening, 2026-07-03). The eyes/capture contract —
      // state/tick/input/startBoard/setDebugVisible/scene/seed — stays UNGATED so every capture path
      // (scripts/capture.mjs, tools/capture-gameplay-clip.mjs, tools/capture-first-session.mjs,
      // tests/eyes/capture.spec.ts) works with debug=0. The cheat lever (forces score/tension/end
      // states) is the ONE thing fenced behind ?debug=1, so a shipped player build can't invoke it via
      // window.__game.cheat(). The only caller, tools/parity-capture.mjs, navigates with debug=1 (see
      // tools/parity/screens.mjs PLAY url), so it keeps working; a non-debug caller gets a no-op that
      // returns false — never a throw.
      if (!debug) return false;
      const s = findGameScene() as unknown as { applyCheat?: (n: string) => void } | null;
      if (!s?.applyCheat) return false;
      s.applyCheat(name);
      return true;
    },
    setDebugVisible(visible: boolean): void {
      findGameScene()?.setDebugVisible(visible);
    },
    get scene(): GameScene | undefined {
      return findGameScene() ?? undefined;
    },
  };

  win().__game = hook;
}

/** Called by GameScene.create() once the board is built so state()/input() target the live sim. */
export function setActiveGameScene(scene: GameScene): void {
  activeScene = scene;
  const hook = win().__game;
  if (hook) {
    hook.ready = true;
    hook.seed = scene.sim ? scene.sim.seed : hook.seed;
  }
}
